import { useState, useEffect, useMemo } from 'react'
import { useTheme } from '../context/ThemeContext'
import { apiConfig } from '../auth/authConfig'
import TankOverview from '../components/TankOverview'
import TankDetails from '../components/TankDetails'
import SalesView from '../components/SalesView'

const CACHE_DURATION_MS = 5 * 60 * 1000

// Get current month as YYYY-MM
const getCurrentMonth = () => {
    const now = new Date()
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
}

function TankDashboard() {
    const { theme } = useTheme()
    const isDark = theme === 'dark'

    const [tankData, setTankData] = useState(null)
    const [salesData, setSalesData] = useState(null)
    const [loading, setLoading] = useState(true)
    const [salesLoading, setSalesLoading] = useState(false)
    const [error, setError] = useState(null)
    const [viewMode, setViewMode] = useState('sales') // 'sales' | 'overview' | 'details'
    const [purchaserFilter, setPurchaserFilter] = useState('all')
    const [fromCache, setFromCache] = useState(false)
    const [salesMonth, setSalesMonth] = useState(getCurrentMonth())

    // Generate month options: last 13 months
    const monthOptions = useMemo(() => {
        const options = []
        const now = new Date()
        for (let i = 0; i < 13; i++) {
            const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
            const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
            const label = d.toLocaleString('default', { month: 'long', year: 'numeric' })
            options.push({ value, label })
        }
        return options
    }, [])

    // --- Cache helpers (match ProductionDashboard.jsx pattern) ---

    const getFromCache = (key) => {
        try {
            const cached = localStorage.getItem(key)
            if (!cached) return null
            const { data, timestamp } = JSON.parse(cached)
            if (Date.now() - timestamp < CACHE_DURATION_MS) return data
            localStorage.removeItem(key)
            return null
        } catch { return null }
    }

    const saveToCache = (key, data) => {
        try {
            localStorage.setItem(key, JSON.stringify({ data, timestamp: Date.now() }))
        } catch {}
    }

    // --- Data fetching ---

    const fetchTankData = async (forceRefresh = false) => {
        setLoading(true)
        setError(null)
        setFromCache(false)

        if (!forceRefresh) {
            const cached = getFromCache('dd_tanks')
            if (cached) {
                setTankData(cached)
                setLoading(false)
                setFromCache(true)
                return
            }
        }

        try {
            const res = await fetch(`${apiConfig.baseUrl}/tanks`)
            if (!res.ok) throw new Error('Failed to fetch tank data')
            const json = await res.json()
            setTankData(json)
            saveToCache('dd_tanks', json)
        } catch (err) {
            setError(err.message)
        } finally {
            setLoading(false)
        }
    }

    const fetchSalesData = async (forceRefresh = false, month = null) => {
        const m = month || salesMonth
        const cacheKey = `dd_tanks_sales_${m}`
        if (!forceRefresh) {
            const cached = getFromCache(cacheKey)
            if (cached) {
                setSalesData(cached)
                return
            }
        }

        setSalesLoading(true)
        try {
            const res = await fetch(`${apiConfig.baseUrl}/tanks/sales?month=${m}`)
            if (!res.ok) throw new Error('Failed to fetch sales data')
            const json = await res.json()
            setSalesData(json)
            saveToCache(cacheKey, json)
        } catch (err) {
            setError(err.message)
        } finally {
            setSalesLoading(false)
        }
    }

    // Fetch tank data on mount
    useEffect(() => {
        fetchTankData()
    }, [])

    // Fetch sales data when month changes
    useEffect(() => {
        fetchSalesData(false, salesMonth)
    }, [salesMonth])

    // --- Purchaser filter ---

    const purchaserOptions = useMemo(() => {
        if (!tankData?.tanks) return []
        const purchasers = new Set()
        tankData.tanks.forEach(t => {
            if (t.purchaser && t.purchaser.trim()) purchasers.add(t.purchaser.trim())
        })
        return [...purchasers].sort()
    }, [tankData])

    const filteredTanks = useMemo(() => {
        if (!tankData?.tanks) return []
        if (purchaserFilter === 'all') return tankData.tanks
        if (purchaserFilter === 'unassigned') return tankData.tanks.filter(t => !t.purchaser || !t.purchaser.trim())
        return tankData.tanks.filter(t => t.purchaser?.trim() === purchaserFilter)
    }, [tankData, purchaserFilter])

    return (
        <div className="space-y-4 sm:space-y-6">
            {/* Header row */}
            <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                    <h2 className={`text-lg sm:text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        Tank Inventory & Sales
                    </h2>
                    {viewMode === 'sales' && salesData?.lastRefreshed ? (
                        <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                            Sales data refreshed {new Date(salesData.lastRefreshed).toLocaleString()}
                            {fromCache && ' (cached)'}
                        </p>
                    ) : tankData?.asOfDate ? (
                        <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                            Tank data as of {new Date(tankData.asOfDate).toLocaleDateString()}
                            {fromCache && ' (cached)'}
                        </p>
                    ) : null}
                </div>
                <button
                    onClick={() => { fetchTankData(true); if (viewMode === 'sales') fetchSalesData(true, salesMonth); }}
                    className="text-xs px-3 py-1.5 rounded bg-blue-600 hover:bg-blue-700 text-white transition"
                    disabled={loading}
                >
                    {loading ? 'Loading...' : 'Refresh'}
                </button>
            </div>

            {/* 3-way toggle pills + month selector */}
            <div className="flex flex-wrap items-center gap-2">
                <div className="flex gap-1">
                    {[
                        { key: 'sales', label: 'Sales' },
                        { key: 'overview', label: 'Oil Tank Data' },
                        { key: 'details', label: 'Oil Tank Details' },
                    ].map(v => (
                        <button
                            key={v.key}
                            onClick={() => setViewMode(v.key)}
                            className={`px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium rounded-lg transition ${
                                viewMode === v.key
                                    ? 'bg-blue-600 text-white'
                                    : isDark
                                        ? 'bg-slate-800 text-gray-400 hover:text-white'
                                        : 'bg-gray-100 text-gray-600 hover:text-gray-900'
                            }`}
                        >
                            {v.label}
                        </button>
                    ))}
                </div>
                {viewMode === 'sales' && (
                    <select
                        value={salesMonth}
                        onChange={e => setSalesMonth(e.target.value)}
                        className={`px-3 py-1.5 sm:py-2 text-xs sm:text-sm font-medium rounded-lg border transition cursor-pointer ${
                            isDark
                                ? 'bg-slate-800 border-slate-700 text-white'
                                : 'bg-white border-gray-300 text-gray-700'
                        }`}
                    >
                        {monthOptions.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                )}
            </div>

            {/* Purchaser filter (only for tank views) */}
            {viewMode !== 'sales' && purchaserOptions.length > 0 && (
                <div className="flex flex-wrap gap-1">
                    <button
                        onClick={() => setPurchaserFilter('all')}
                        className={`px-3 py-1 text-xs rounded transition ${
                            purchaserFilter === 'all'
                                ? 'bg-blue-600 text-white'
                                : isDark ? 'bg-slate-800 text-gray-400 hover:text-white' : 'bg-gray-100 text-gray-600 hover:text-gray-900'
                        }`}
                    >
                        All
                    </button>
                    {purchaserOptions.map(p => (
                        <button
                            key={p}
                            onClick={() => setPurchaserFilter(p)}
                            className={`px-3 py-1 text-xs rounded transition ${
                                purchaserFilter === p
                                    ? 'bg-blue-600 text-white'
                                    : isDark ? 'bg-slate-800 text-gray-400 hover:text-white' : 'bg-gray-100 text-gray-600 hover:text-gray-900'
                            }`}
                        >
                            {p}
                        </button>
                    ))}
                    <button
                        onClick={() => setPurchaserFilter('unassigned')}
                        className={`px-3 py-1 text-xs rounded transition ${
                            purchaserFilter === 'unassigned'
                                ? 'bg-blue-600 text-white'
                                : isDark ? 'bg-slate-800 text-gray-400 hover:text-white' : 'bg-gray-100 text-gray-600 hover:text-gray-900'
                        }`}
                    >
                        Unassigned
                    </button>
                </div>
            )}

            {/* Error state */}
            {error && !tankData && (
                <div className="bg-red-900/20 border border-red-800 rounded-lg p-4 text-red-400">
                    <p className="font-semibold">Error loading data</p>
                    <p className="text-sm mt-1">{error}</p>
                    <button
                        onClick={() => fetchTankData(true)}
                        className="mt-3 text-sm bg-red-800 hover:bg-red-700 text-white px-3 py-1 rounded transition"
                    >
                        Retry
                    </button>
                </div>
            )}

            {/* Loading state */}
            {loading && !tankData && (
                <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                </div>
            )}

            {/* Active view */}
            {tankData && viewMode === 'overview' && (
                <TankOverview kpis={tankData.kpis} trends={tankData.trends} tanks={filteredTanks} fleetTrend={tankData.fleetTrend} />
            )}
            {tankData && viewMode === 'details' && (
                <TankDetails tanks={filteredTanks} />
            )}
            {viewMode === 'sales' && (
                <SalesView data={salesData} loading={salesLoading} selectedMonth={salesMonth} />
            )}
        </div>
    )
}

export default TankDashboard
